import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import { string, object, record, optional, array, boolean, number, union, literal } from 'valibot';
import { HttpClient, HttpXhrBackend } from '@angular/common/http';
import { of } from 'rxjs';
const CONFIG_LOADER = new InjectionToken('CONFIG_LOADER');
class BaseConfigService {
  constructor(config) {
    this.config = config;
  }
  get() {
    return this.config;
  }
  static invalidConfigLoaderError(configName) {
    return new Error(`Your are trying to use the ${configName} config service, but your store specific ConfigLoaderService provided does not support it. Please check your ConfigLoaderService implements the ${configName}Loader interface.`);
  }
}
const builderIOApiKeyType = string();
const BuilderConfigSchema = object({
  builderIOApiKey: builderIOApiKeyType,
  flavorEnv: record(string(), object({
    builderIOApiKey: builderIOApiKeyType
  })),
  env: string(),
  dynamicBuilderIOApiKey: optional(string()),
  // Supports the use of dynamic environments using BUILDTIME variables
  flavor: string()
});
function isBuilderConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getBuilderConfig' in config;
}
let BuilderConfigService = /*#__PURE__*/(() => {
  class BuilderConfigService extends BaseConfigService {
    constructor(builderConfigLoader) {
      if (!isBuilderConfigLoader(builderConfigLoader)) {
        throw BaseConfigService.invalidConfigLoaderError('BuilderConfig');
      }
      const config = builderConfigLoader.getBuilderConfig();
      const flavorEnvObj = config.flavorEnv[`${config.flavor}-${config.env}`];
      if (flavorEnvObj) {
        Object.assign(config, flavorEnvObj);
      } else {
        //FIXME: get logger into this class without circular dependency logger.info('Builder config: Flavor env specific key not found for builder, using default builderIOApiKey');
      }
      const dynamicKey = config.dynamicBuilderIOApiKey;
      if (dynamicKey) {
        config.builderIOApiKey = dynamicKey;
      }
      if (!config.builderIOApiKey) {
        console.error({
          msg: 'Builder config: builderIOApiKey is not set after resolving env',
          config
        });
      }
      super(config);
    }
    static {
      this.ɵfac = function BuilderConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BuilderConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BuilderConfigService,
        factory: BuilderConfigService.ɵfac
      });
    }
  }
  return BuilderConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const CheckoutConfigSchema = object({
  checkoutMethods: array(string()),
  expressCheckoutMethods: array(string()),
  xApi: object({
    clientId: string(),
    cartUrl: string(),
    cartTokenKey: string(),
    cartProviderKey: string(),
    xApiKey: string()
  }),
  taxes: object({
    entity: string(),
    transaction_type: string(),
    tax_inclusive: boolean()
  }),
  username: string(),
  password: string()
});
function isCheckoutConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getCheckoutConfig' in config;
}
let CheckoutConfigService = /*#__PURE__*/(() => {
  class CheckoutConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isCheckoutConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('CheckoutConfig');
      }
      const config = configLoaderService.getCheckoutConfig();
      super(config);
    }
    static {
      this.ɵfac = function CheckoutConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CheckoutConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CheckoutConfigService,
        factory: CheckoutConfigService.ɵfac
      });
    }
  }
  return CheckoutConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isContentfulConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getContentfulConfig' in config;
}
let ContentfulConfigService = /*#__PURE__*/(() => {
  class ContentfulConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isContentfulConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('ContentfulConfig');
      }
      const config = configLoaderService.getContentfulConfig();
      super(config);
    }
    static {
      this.ɵfac = function ContentfulConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ContentfulConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ContentfulConfigService,
        factory: ContentfulConfigService.ɵfac
      });
    }
  }
  return ContentfulConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isStoreSearchConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getStoreSearchConfig' in config;
}
let StoreSearchConfigService = /*#__PURE__*/(() => {
  class StoreSearchConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isStoreSearchConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('StoreSearchConfig');
      }
      const config = configLoaderService.getStoreSearchConfig();
      super(config);
    }
    static {
      this.ɵfac = function StoreSearchConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || StoreSearchConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: StoreSearchConfigService,
        factory: StoreSearchConfigService.ɵfac
      });
    }
  }
  return StoreSearchConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isFiltersConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getFiltersConfig' in config;
}
let FiltersConfigService = /*#__PURE__*/(() => {
  class FiltersConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isFiltersConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('FiltersConfig');
      }
      const config = configLoaderService.getFiltersConfig();
      super(config);
    }
    static {
      this.ɵfac = function FiltersConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FiltersConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FiltersConfigService,
        factory: FiltersConfigService.ɵfac
      });
    }
  }
  return FiltersConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isLegacyConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getLegacyConfig' in config;
}
let LegacyConfigService = /*#__PURE__*/(() => {
  class LegacyConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isLegacyConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('LegacyConfig');
      }
      const config = configLoaderService.getLegacyConfig();
      super(config);
    }
    static {
      this.ɵfac = function LegacyConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LegacyConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LegacyConfigService,
        factory: LegacyConfigService.ɵfac
      });
    }
  }
  return LegacyConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isLocalizationConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getLocalizationConfig' in config;
}
let LocalizationConfigService = /*#__PURE__*/(() => {
  class LocalizationConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isLocalizationConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('LocalizationConfig');
      }
      const config = configLoaderService.getLocalizationConfig();
      super(config);
    }
    static {
      this.ɵfac = function LocalizationConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LocalizationConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LocalizationConfigService,
        factory: LocalizationConfigService.ɵfac
      });
    }
  }
  return LocalizationConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isSamConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getSamConfigContext' in config;
}
let SamConfigService = /*#__PURE__*/(() => {
  class SamConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isSamConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('SamConfig');
      }
      const config = configLoaderService.getSamConfigContext();
      super(config);
    }
    static {
      this.ɵfac = function SamConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SamConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SamConfigService,
        factory: SamConfigService.ɵfac
      });
    }
  }
  return SamConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isStoreConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getStoreConfig' in config;
}
let StoreConfigService = /*#__PURE__*/(() => {
  class StoreConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isStoreConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('StoreConfig');
      }
      const config = configLoaderService.getStoreConfig();
      super(config);
    }
    getCustomRoutes() {
      return this.get().customRoutes;
    }
    getHomeModuleCarousel() {
      return this.get().homeModuleCarousel;
    }
    static {
      this.ɵfac = function StoreConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || StoreConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: StoreConfigService,
        factory: StoreConfigService.ɵfac
      });
    }
  }
  return StoreConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isMaintenanceConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getMaintenanceConfig' in config;
}
let MaintenanceConfigService = /*#__PURE__*/(() => {
  class MaintenanceConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isMaintenanceConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('MaintenanceConfig');
      }
      const config = configLoaderService.getMaintenanceConfig();
      super(config);
    }
    static {
      this.ɵfac = function MaintenanceConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MaintenanceConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MaintenanceConfigService,
        factory: MaintenanceConfigService.ɵfac
      });
    }
  }
  return MaintenanceConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isStoreSpecificConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getStoreSpecificConfig' in config;
}
let StoreSpecificConfigService = /*#__PURE__*/(() => {
  class StoreSpecificConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isStoreSpecificConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('StoreSpecificConfig');
      }
      const config = configLoaderService.getStoreSpecificConfig();
      super(config);
    }
    static {
      this.ɵfac = function StoreSpecificConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || StoreSpecificConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: StoreSpecificConfigService,
        factory: StoreSpecificConfigService.ɵfac
      });
    }
  }
  return StoreSpecificConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isFeatureFlagsConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getFeatureFlagsConfig' in config;
}
let FeatureFlagsConfigService = /*#__PURE__*/(() => {
  class FeatureFlagsConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isFeatureFlagsConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('FeatureFlagsConfig');
      }
      const config = configLoaderService.getFeatureFlagsConfig();
      super(config);
    }
    static {
      this.ɵfac = function FeatureFlagsConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FeatureFlagsConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FeatureFlagsConfigService,
        factory: FeatureFlagsConfigService.ɵfac
      });
    }
  }
  return FeatureFlagsConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function isBffConfigLoader(config) {
  return typeof config === 'object' && config !== null && 'getBffConfig' in config;
}
let BffConfigService = /*#__PURE__*/(() => {
  class BffConfigService extends BaseConfigService {
    constructor(configLoaderService) {
      if (!isBffConfigLoader(configLoaderService)) {
        throw BaseConfigService.invalidConfigLoaderError('BffConfig');
      }
      const config = configLoaderService.getBffConfig();
      const envObj = config.envs[`${config.env}`];
      if (envObj) {
        Object.assign(config, envObj);
      } else {
        //FIXME: get logger into this class without circular dependency logger.info('BFF config: env not found', config);
      }
      if (!config.url) {
        console.error({
          msg: 'BFF config: url is not set after resolving env',
          config
        });
      }
      super(config);
    }
    static {
      this.ɵfac = function BffConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BffConfigService)(i0.ɵɵinject(CONFIG_LOADER));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BffConfigService,
        factory: BffConfigService.ɵfac
      });
    }
  }
  return BffConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ConfigModule = /*#__PURE__*/(() => {
  class ConfigModule {
    static {
      this.ɵfac = function ConfigModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ConfigModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: ConfigModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [SamConfigService, StoreConfigService, FeatureFlagsConfigService, BuilderConfigService, ContentfulConfigService, StoreSearchConfigService, FiltersConfigService, CheckoutConfigService, LocalizationConfigService, LegacyConfigService, MaintenanceConfigService, StoreSpecificConfigService, BffConfigService]
      });
    }
  }
  return ConfigModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const ContentfulConfigSchema = array(object({
  locale: string(),
  space: string(),
  accessToken: string()
}));
const storeSearchSchema = object({
  userID: string(),
  locale: string(),
  currency: string(),
  env: string()
});
const FilterOptionSchema = object({
  label: string(),
  checked: boolean(),
  value: string() // Ensure 'value' corresponds to the field you're targeting
});
const FilterSchema = object({
  name: string(),
  value: string(),
  type: string(),
  groupBy: string(),
  options: array(FilterOptionSchema),
  excludedCategories: array(number())
});
const FiltersListSchema = array(FilterSchema);
const FiltersConfigSchema = object({
  filtersList: FiltersListSchema
});
const LocalizationSchema = object({
  currency: string(),
  v4Locale: string(),
  territories: array(string()),
  currentLang: string()
});

// Common v4 and v5 properties
/** @TODO We SHOULD manage better the secrets and try to avoid having it in the front side */
const SpecificApiContextSchema = {
  projectId: string(),
  territoryId: string(),
  apiKey: string(),
  store_id: number(),
  baseUrl: optional(string())
};
// Specific v4 properties
const SamContextV4Schema = object({
  clientId: string(),
  assetsUrl: string(),
  zendeskGetFormBaseEndpoint: string(),
  zendeskSendFormBaseEndpoint: string(),
  zendeskFormID: string(),
  storeSearchBaseUrl: string(),
  pepStorageKey: string(),
  apiVersion: string(),
  environment: string(),
  machineName: optional(string()),
  userID: string(),
  locale: string(),
  ...SpecificApiContextSchema // Directly spread the properties from SpecificApiContextSchema
});
// Specific v5 properties
const SamContextV5Schema = object({
  clientSecret: string(),
  grantTypeM2M: string(),
  grantTypeStore: string(),
  clientIdM2M: string(),
  clientIdStore: string(),
  paymentBaseUrl: string(),
  komojuBaseUrl: string(),
  ...SpecificApiContextSchema // Directly spread the properties from SpecificApiContextSchema
});
const ApiContextSchema = object({
  v4: SamContextV4Schema,
  v5: SamContextV5Schema
});
const CredentialsSchema = object({
  implementation: union([literal('local-storage'), literal('session-storage'), literal('cookie')]),
  expiration: number()
});
const CredentialsConfigSchema = object({
  accessToken: CredentialsSchema
});
const CartStorageConfigSchema = object({
  implementation: union([literal('v4-local-storage'), literal('x-api')])
});
const SamConfigSchema = object({
  apiContext: ApiContextSchema,
  credentialsConfig: CredentialsConfigSchema,
  cartStorageConfig: CartStorageConfigSchema
});
const StoreConfigCustomRoutesSchema = object({
  cart: optional(string()),
  account: optional(string()),
  login: optional(string()),
  register: optional(string()),
  forgotpassword: optional(string())
});
const StoreConfigSchema = object({
  name: string(),
  customRoutes: optional(StoreConfigCustomRoutesSchema),
  hasNewsletter: boolean(),
  hasPromotions: boolean(),
  maxOrderQuantity: number(),
  maxItemQuantity: number(),
  currency: optional(string()),
  browserLogLevel: optional(union([literal('fatal'), literal('error'), literal('warn'), literal('info'), literal('debug'), literal('trace')])),
  homeModuleCarousel: optional(object({
    useFeedId: boolean(),
    feedId: number(),
    carouselLimit: number(),
    title: string()
  }))
});
const FeatureFlagsSchema = object({
  filterPOHProductsInSearch: optional(boolean())
});
const maintenanceSchema = array(object({
  maintenanceState: object({
    enabled: boolean()
  }),
  timeStamp: number()
}));
const BffConfigSchema = object({
  envs: record(string(), object({
    url: string()
  })),
  env: string(),
  url: string()
});
class CheckoutConfigServiceMock {
  constructor() {
    this.config = {
      checkoutMethods: ['standard'],
      expressCheckoutMethods: ['paypal', 'google_pay', 'apple_pay'],
      xApi: {
        clientId: '15105302',
        cartUrl: 'https://x-api.scalefast.com/customer/provider',
        cartTokenKey: 'sf-cart-token',
        cartProviderKey: 'sf-cart-provider',
        xApiKey: '36QEdhO8XD4I9NdjpdbIH8IejcYs4xEg40vD8TPN'
      },
      taxes: {
        entity: 'Scalefast Inc',
        transaction_type: 'b2b',
        tax_inclusive: true
      },
      username: 'architects@scalefast.com',
      password: 'a123456S_'
    };
  }
  get() {
    return this.config;
  }
}
class ContentfulConfigServiceMock {
  constructor() {
    this.config = [{
      locale: 'en-US',
      space: 'wcspu3f3pfhu',
      accessToken: 'MT2xvwVaHosUjyWhTQRKCjUXc2AEeILXWall9YPijSk'
    }];
  }
  get() {
    return this.config;
  }
}
class ContentfulResolverMock {
  resolve() {
    return {
      slug: '/es-es/salud-dental-sostenibilidad',
      pagePathId: '75l43buLAXDAwmp3QZZyfi',
      locale: 'es-ES',
      contentId: '12JWDqjAYby0vK0AR74AkD',
      contentType: 'articleOverview',
      breadcrumb: 'salud-dental-sostenibilidad',
      contentfulContent: {
        content: {
          title: 'Article Overview - Our Ambition',
          description: '\n <div class="ambitions-title-container ob-html-block article-body">\n  <div class="ambitions-title-container-left"> \n         <div class="ambitions-title">\n           <h1 style="font-weight: 700;font-family: Hurme Regular;margin-bottom: 0;">Nuestro objetivo:</h1>\n            </div>\n<div class="ambitions-subtitle">Hacer que todo el mundo adquiera hábitos saludables de cuidado bucal.</div>\n  </div>\n     <span class="text-container-right">\n\n          <div class="article-content-habits">\n            Llevar a cabo una buena rutina de cuidado dental va más allá del hecho de conseguir una buena dentadura. Tener una buena higiene dental no solo es esencial para nuestro bienestar, sino que, además, influye, positivamente, tanto en nuestra vida, como en nuestro amor propio, nuestro habla, nuestra habilidad para sonreír, nuestros sentidos del olfato y del gusto, y nuestra masticación. Nuestro objetivo es muy simple: ayudar a más personas en todo el mundo a comprender la importancia de las sonrisas saludables y cómo las pequeñas acciones diarias pueden tener un gran impacto en la vida de las personas y nuestras comunidades. Para ello, nos hemos propuesto educar y ayudar, de aquí al año 2030, a nada menos que 2000 millones de personas ha adquirir una buena higiene dental al tiempo que promovemos un consumo responsable durante el proceso.\n      </div>\n\n         </span>\n  </div>\n',
          thumbnailImage: '//images.ctfassets.net/4gsr0wtgbzc1/l5j7cR8daOni4vTc6LhXA/10c9e9759f017604db8f9970d40535bf/Sustainability_Hero_Image.png'
        }
      }
    };
  }
}
class CustomerServiceMock {
  getCustomRoutes() {
    //
  }
  getHybridStoreCredentials() {
    return '';
  }
}
const StoreSearchParamsMock = {
  userID: '123622',
  locale: 'en_ROW',
  currency: 'USD',
  env: 'prod',
  count: 16,
  offset: 1
};
class StoreSearchConfigServiceMock {
  constructor() {
    this.config = {
      userID: '17332789',
      locale: 'it_IT',
      currency: 'EUR',
      env: 'frontv5testing-local'
    };
  }
  get() {
    return this.config;
  }
}
const FiltersCriteriaMock = [{
  name: 'Sort by',
  value: 'sort',
  type: 'select',
  groupBy: 'sort',
  options: [{
    label: 'Featured (newest first)',
    checked: false,
    value: 'featured'
  }, {
    label: 'Release Date',
    checked: false,
    value: 'release_date'
  }, {
    label: 'Low to high',
    checked: false,
    value: 'price:asc'
  }, {
    label: 'High to Low',
    checked: false,
    value: 'price:desc'
  }]
}];
const FilteredProductsMock = {
  total: 16,
  products: Array(12).fill({
    country: 'ROW',
    id: '604967',
    imageUrl: 'https://d2f3d56fenwb91.cloudfront.net/public/assets/user/12576212/image/4351fc0353f5117fb8ef15fb8bf9a000.jpg',
    descriptions: [{
      title: 'Title',
      short: 'Short Description'
    }],
    stock: {
      stock: '8',
      policy: 'DENY_PURCHASE',
      current_stock: '10'
    },
    price: {
      country: 'ROW',
      currency: 'USD',
      currencies: [{
        currency: 'USD',
        prices: [{
          oldPrice: '254.98',
          price: '119.99'
        }]
      }],
      includesTaxes: false,
      prices: [{
        price: 19.99,
        license: 'NL'
      }]
    }
  })
};
const ActivatedRouterWithFiltersMock = {
  snapshot: {
    data: {
      category: {
        id: '45033',
        name: {
          ES: 'Name'
        }
      }
    },
    queryParamMap: new Map([['filterColor', 'red'], ['filterBrand', 'nike']])
  }
};
class FiltersConfigServiceMock {
  constructor() {
    this.config = {
      filtersList: [{
        name: 'color',
        value: 'red',
        type: '',
        groupBy: '',
        options: [{
          label: '',
          checked: true,
          value: ''
        }],
        excludedCategories: [1]
      }, {
        name: 'size',
        value: 'small',
        type: '',
        groupBy: '',
        options: [{
          label: '',
          checked: true,
          value: ''
        }],
        excludedCategories: [1]
      }]
    };
  }
  get() {
    return this.config;
  }
  getFilters() {
    return this.config.filtersList;
  }
}
class SamConfigServiceMock {
  constructor() {
    this.config = {
      apiContext: {
        v4: {
          userID: '17332789',
          clientId: 'A233F84AB42713CF4C54AE372FA5E3AE',
          projectId: '1246',
          territoryId: 'IT',
          store_id: 2,
          assetsUrl: 'https://d2f3d56fenwb91.cloudfront.net/public/assets',
          apiVersion: 'v10',
          apiKey: '61DDBC7A51AE2803A47A3AF4EBF239AA',
          zendeskGetFormBaseEndpoint: '/zendesk/ticket_form',
          zendeskSendFormBaseEndpoint: '/zendesk/ticket',
          zendeskFormID: '5789758078994',
          pepStorageKey: '',
          storeSearchBaseUrl: 'https://2s8gancjl1.execute-api.eu-central-1.amazonaws.com/default/StoreSearch',
          locale: 'it_IT',
          environment: 'integration',
          machineName: 'frontv5testing'
        },
        v5: {
          clientSecret: 'ec3c63ec407ac738af5df0a67f12ef894179464d89767c7bdf124818980e411e',
          projectId: '1246',
          territoryId: 'IT',
          apiKey: '5457D4CDE0F28FC5EA15A07F65EF4DAF',
          store_id: 2,
          grantTypeM2M: 'password',
          grantTypeStore: 'client_credentials',
          clientIdM2M: 'c068011f63cf932a47096317a86dea7b',
          clientIdStore: '8d8e0cdb52b3d0f1d7553312e1e82385',
          baseUrl: 'https://frontv5testing-api.scalefast.ninja',
          paymentBaseUrl: 'https://pay-v5.scalefast.ninja',
          komojuBaseUrl: 'https://komoju.com'
        }
      },
      credentialsConfig: {
        accessToken: {
          implementation: 'local-storage',
          expiration: 100000
        }
      },
      cartStorageConfig: {
        implementation: 'x-api'
      }
    };
  }
  get() {
    return this.config;
  }
}
class StoreConfigServiceMock {
  constructor() {
    this.config = {
      name: 'Merchandise Store',
      customRoutes: {
        cart: '',
        account: '',
        login: '',
        forgotpassword: ''
      },
      hasNewsletter: true,
      hasPromotions: true,
      maxOrderQuantity: 10,
      maxItemQuantity: 10,
      currency: 'EUR'
    };
  }
  get() {
    return this.config;
  }
  getCustomRoutes() {
    return this.config.customRoutes;
  }
}
class LegacyConfigServiceMock {
  constructor() {
    this.config = {
      hasPromotions: true,
      freeShippingValue: 69.99,
      klarnaMinValue: 105,
      klarnaInstallments: 3,
      metacolorReferences: {
        White: '#fff',
        Black: '#000'
      },
      menu: {
        items: [{
          children: [{
            children: [{
              children: [],
              id: 'masks-women',
              label: 'Masks for women',
              path: '/apparel/face-masks/mask-women',
              productFilter: '"cat;88644|all"',
              visible: false
            }, {
              children: [],
              id: 'mask-men',
              label: 'Mask for men',
              path: '/apparel/face-masks/mask-men',
              productFilter: '"cat:88645|all"',
              visible: false
            }],
            id: 'face-masks',
            label: 'Face masks',
            path: '/apparel/face-masks',
            productFilter: '"cat:88641|all"',
            visible: true
          }, {
            children: [],
            id: 'hoodies',
            label: 'Hoodies',
            path: '/apparel/hoodies',
            productFilter: '"cat;88640|all"',
            visible: true
          }],
          id: '',
          label: 'GAMES',
          path: 'https://eu-storefront.scalefast.com/basic-store/eu/all-games',
          visible: true,
          productFilter: ''
        }, {
          children: [{
            children: [],
            id: 'mugs',
            label: 'Mugs',
            path: '/merchandise/mugs',
            productFilter: '"cat:88636|all"',
            visible: true
          }, {
            children: [],
            id: 'mousepads',
            label: 'Mousepads',
            path: '/merchandise/mousepads',
            productFilter: '"cat:88637|all"',
            visible: true
          }, {
            children: [],
            id: 'stickers',
            label: 'Bottles',
            path: '/merchandise/bottles',
            productFilter: '"cat:88642|all"',
            visible: true
          }],
          id: '',
          label: 'MERCHANDISE',
          path: '/merchandise',
          productFilter: '"cat:45035|all"',
          visible: true
        }, {
          children: [],
          id: 'limited-editions',
          label: 'LIMITED EDITIONS',
          path: '/limited-editions',
          productFilter: '"feed:67270|all"',
          visible: true
        }]
      },
      homeSlideshow: {
        items: [{
          btnTitle: 'VIEW ALL',
          desc: 'New arrivals worldwide',
          id: '',
          image: 'https://d2f3d56fenwb91.cloudfront.net/public/assets/uploads/merchandise-store/WHvOnmzrScreenshot2023-03-03at10.19.07.jpg',
          link: '/new-arrivals',
          refID: '23232323',
          title: 'NEW ARRIVALS',
          type: 'other',
          visible: true
        }]
      },
      homeModules: [{
        abtest: false,
        count: 4,
        link: '/featured-products',
        order: -1,
        prodsFilter: 'feed:67265',
        randomize: false,
        title: 'FEATURED PRODUCTS',
        titleMerchant: '',
        visible: true
      }],
      products: [{
        categories: [{
          categoryID: '88386',
          externalID: '1',
          categoryName: 'Merchandise',
          parent_cat_id: '-2'
        }, {
          categoryID: '88636',
          externalID: '-1',
          categoryName: 'Mugs',
          parent_cat_id: '88386'
        }],
        country: 'REU',
        descriptions: [{
          lang: 'FR',
          title: 'FR MUG01 TEST',
          short: '(FRENCH) A mug is a type of cup typically used for drinking hot drinks, such as coffee, hot chocolate, or tea. Mugs usually have handles and hold a larger amount of fluid than other types of cup. ... A travel mug is insulated and has a cover with a small sipping opening to prevent spills.',
          long: '(FRENCH LANGUAGE)<br><br>Early mugs <br><br>Wooden mugs were produced probably from the earliest days of woodworking, but most of them have not survived intact.[3][4]<br>The first pottery was shaped by hand and was later facilitated by the invention of the potter&#39;s wheel (date unknown, between 6,500 and 3000 BCE). It was relatively easy to add a handle to a cup in the process thus producing a mug. For example, a rather advanced, decorated clay mug from 4000–5000 BCE was found in Greece.[5]<br>Ancestral Pueblo (Anasazi) mugs from SW Colorado, made between 1000 and 1280 CE. The meaning of the carving in the handles is as yet unknown, but it is probably not functional.<br>The biggest disadvantage of those clay mugs was thick walls unfit for the mouth. The walls were thinned with development of metalworking techniques. Metal mugs were produced from bronze,[6] silver, gold,[7] and even lead,[8] starting from roughly 2000 BCE, but were hard to use with hot drinks.<br>The invention of porcelain around 600 CE in China brought a new era of thin-walled mugs suitable both for cold and hot liquids, which are enjoyed today.<br><br>Shaving mug<br>A shaving scuttle and shaving mug were developed around the 19th century; the first patent for a shaving mug is dated 1867.[10] As hot water was not common in many households, one way to provide hot lather was to use a scuttle or mug. A traditional scuttle resembles a teapot with a wide spout where hot water is poured in; this is where it differs from a shaving mug, which has no spout. Both shaving scuttles and mugs usually have a handle, but some have none. Shaving mugs often look like a standard mug, however, some also have a built in brush rest, so the brush does not sit in lather. Modern versions of the scuttle are in limited production, usually by independent potters working in small volumes.[11]<br><br>At the top of the scuttle or mug is a soap holder. Traditionally, it was used with a hard block of shaving soap (rather than soft soap or cream) and therefore had drain holes at the bottom. Later scuttles and mugs do not include the holes, and thus can be used with creams and soft soaps. Some scuttles and mugs have concentric circles on the bottom, which retain some water thus helping to build lather.[11]<br><br>In use, the shaving brush is dunked into the wide spout, allowing it to soak into the water and heat up. The soap is placed in the soap holder. When needed, one can take the brush and brush it against the soap, bringing up a layer of lather; excess water is drained back. This allows conservation of water and soap, whilst retaining enough heat to ensure a long shave.<br><br><br><br>Tiki mugs<br>Tiki mugs, drinking vessels usually made of ceramics, originated in mid-20th century tropical themed restaurants and tiki bars. The term "Tiki mugs" is a generic, blanket term for sculptural drink ware that depict imagery from Melanesia, Micronesia, or Polynesia, and more recently anything tropical or related to surfing. Often sold as souvenirs, tiki mugs are highly collectable. Modern manufacturers include Muntiki and Tiki Farm. Individual artists, such as Van Tiki, also produced limited one-of-a-kind hand sculpted mugs.[12]<br><br>Travel mugs<br><br>Travel mugs (introduced in the 1980s) generally employ thermal insulation properties for transporting hot or cold liquids. Similar to a vacuum flask, a travel mug is usually well-insulated and completely enclosed to prevent spillage[13] or leaking, but will generally have an opening in the cover through which the contents can be consumed during transportation without spillage. As the primary mechanism by which hot (not warm) beverages lose heat is evaporation, a lid serves a vital role in keeping the drink hot; even a thin plastic one which conducts heat quite quickly.<br><br>Mugs with inner and outer walls, but not vacuum treated, are generally called double wall mugs. Usually stainless steel will be used for the inner wall while outer wall can be stainless steel, plastic, or even embedded with other materials.<br><br>Mugs designed for use when driving are called auto mugs or commuter mugs. Travel mugs have a spill-proof lid with a sipping opening[14] and in many cases, a narrower base, so that they will fit into the cup-holders that are built into many vehicles.[citation needed] Additional criteria for evaluating auto mugs include: they must be easy to open single-handedly (to prevent distractions while driving), include a fill line (to prevent over-filling, which contributes to leaking), preferably have no handles (no-handled mugs are easier to grab while driving), should not obstruct a driver&#39;s view of the road when he or she is drinking, and - with regard to cup-holders be able to fit, stably, into a wide range of mug holders.[15][16][17]',
          seo: {
            title: '',
            meta_description: '',
            url: ''
          },
          sections: [{
            i18nTitleTag: 'PRODUCTS.DESCRIPTION',
            content: '(FRENCH LANGUAGE)<br><br>Early mugs <br><br>Wooden mugs were produced probably from the earliest days of woodworking, but most of them have not survived intact.[3][4]<br>The first pottery was shaped by hand and was later facilitated by the invention of the potter&#39;s wheel (date unknown, between 6,500 and 3000 BCE). It was relatively easy to add a handle to a cup in the process thus producing a mug. For example, a rather advanced, decorated clay mug from 4000–5000 BCE was found in Greece.[5]<br>Ancestral Pueblo (Anasazi) mugs from SW Colorado, made between 1000 and 1280 CE. The meaning of the carving in the handles is as yet unknown, but it is probably not functional.<br>The biggest disadvantage of those clay mugs was thick walls unfit for the mouth. The walls were thinned with development of metalworking techniques. Metal mugs were produced from bronze,[6] silver, gold,[7] and even lead,[8] starting from roughly 2000 BCE, but were hard to use with hot drinks.<br>The invention of porcelain around 600 CE in China brought a new era of thin-walled mugs suitable both for cold and hot liquids, which are enjoyed today.<br><br>Shaving mug<br>A shaving scuttle and shaving mug were developed around the 19th century; the first patent for a shaving mug is dated 1867.[10] As hot water was not common in many households, one way to provide hot lather was to use a scuttle or mug. A traditional scuttle resembles a teapot with a wide spout where hot water is poured in; this is where it differs from a shaving mug, which has no spout. Both shaving scuttles and mugs usually have a handle, but some have none. Shaving mugs often look like a standard mug, however, some also have a built in brush rest, so the brush does not sit in lather. Modern versions of the scuttle are in limited production, usually by independent potters working in small volumes.[11]<br><br>At the top of the scuttle or mug is a soap holder. Traditionally, it was used with a hard block of shaving soap (rather than soft soap or cream) and therefore had drain holes at the bottom. Later scuttles and mugs do not include the holes, and thus can be used with creams and soft soaps. Some scuttles and mugs have concentric circles on the bottom, which retain some water thus helping to build lather.[11]<br><br>In use, the shaving brush is dunked into the wide spout, allowing it to soak into the water and heat up. The soap is placed in the soap holder. When needed, one can take the brush and brush it against the soap, bringing up a layer of lather; excess water is drained back. This allows conservation of water and soap, whilst retaining enough heat to ensure a long shave.<br><br><br><br>Tiki mugs<br>Tiki mugs, drinking vessels usually made of ceramics, originated in mid-20th century tropical themed restaurants and tiki bars. The term "Tiki mugs" is a generic, blanket term for sculptural drink ware that depict imagery from Melanesia, Micronesia, or Polynesia, and more recently anything tropical or related to surfing. Often sold as souvenirs, tiki mugs are highly collectable. Modern manufacturers include Muntiki and Tiki Farm. Individual artists, such as Van Tiki, also produced limited one-of-a-kind hand sculpted mugs.[12]<br><br>Travel mugs<br><br>Travel mugs (introduced in the 1980s) generally employ thermal insulation properties for transporting hot or cold liquids. Similar to a vacuum flask, a travel mug is usually well-insulated and completely enclosed to prevent spillage[13] or leaking, but will generally have an opening in the cover through which the contents can be consumed during transportation without spillage. As the primary mechanism by which hot (not warm) beverages lose heat is evaporation, a lid serves a vital role in keeping the drink hot; even a thin plastic one which conducts heat quite quickly.<br><br>Mugs with inner and outer walls, but not vacuum treated, are generally called double wall mugs. Usually stainless steel will be used for the inner wall while outer wall can be stainless steel, plastic, or even embedded with other materials.<br><br>Mugs designed for use when driving are called auto mugs or commuter mugs. Travel mugs have a spill-proof lid with a sipping opening[14] and in many cases, a narrower base, so that they will fit into the cup-holders that are built into many vehicles.[citation needed] Additional criteria for evaluating auto mugs include: they must be easy to open single-handedly (to prevent distractions while driving), include a fill line (to prevent over-filling, which contributes to leaking), preferably have no handles (no-handled mugs are easier to grab while driving), should not obstruct a driver&#39;s view of the road when he or she is drinking, and - with regard to cup-holders be able to fit, stably, into a wide range of mug holders.[15][16][17]'
          }]
        }, {
          lang: 'EN',
          title: 'MUG01 test',
          short: 'A mug is a type of cup typically used for drinking hot drinks, such as coffee, hot chocolate, or tea. Mugs usually have handles and hold a larger amount of fluid than other types of cup. ... A travel mug is insulated and has a cover with a small sipping opening to prevent spills.',
          long: '<u>Early mugs</u><br><br>Wooden mugs were produced probably from the earliest days of woodworking, but most of them have not survived intact.[3][4]<br>The first pottery was shaped by hand and was later facilitated by the invention of the potter&#39;s wheel (date unknown, between 6,500 and 3000 BCE). It was relatively easy to add a handle to a cup in the process thus producing a mug. For example, a rather advanced, decorated clay mug from 4000–5000 BCE was found in Greece.[5]<br>Ancestral Pueblo (Anasazi) mugs from SW Colorado, made between 1000 and 1280 CE. The meaning of the carving in the handles is as yet unknown, but it is probably not functional.<br>The biggest disadvantage of those clay mugs was thick walls unfit for the mouth. The walls were thinned with development of metalworking techniques. Metal mugs were produced from bronze,[6] silver, gold,[7] and even lead,[8] starting from roughly 2000 BCE, but were hard to use with hot drinks.<br>The invention of porcelain around 600 CE in China brought a new era of thin-walled mugs suitable both for cold and hot liquids, which are enjoyed today.<br><br><u>Shaving mug</u><br>A shaving scuttle and shaving mug were developed around the 19th century; the first patent for a shaving mug is dated 1867.[10] As hot water was not common in many households, one way to provide hot lather was to use a scuttle or mug. A traditional scuttle resembles a teapot with a wide spout where hot water is poured in; this is where it differs from a shaving mug, which has no spout. Both shaving scuttles and mugs usually have a handle, but some have none. Shaving mugs often look like a standard mug, however, some also have a built in brush rest, so the brush does not sit in lather. Modern versions of the scuttle are in limited production, usually by independent potters working in small volumes.[11]<br><br>At the top of the scuttle or mug is a soap holder. Traditionally, it was used with a hard block of shaving soap (rather than soft soap or cream) and therefore had drain holes at the bottom. Later scuttles and mugs do not include the holes, and thus can be used with creams and soft soaps. Some scuttles and mugs have concentric circles on the bottom, which retain some water thus helping to build lather.[11]<br><br>In use, the shaving brush is dunked into the wide spout, allowing it to soak into the water and heat up. The soap is placed in the soap holder. When needed, one can take the brush and brush it against the soap, bringing up a layer of lather; excess water is drained back. This allows conservation of water and soap, whilst retaining enough heat to ensure a long shave.<br><br><br><br><u>Tiki mugs</u><br>Tiki mugs, drinking vessels usually made of ceramics, originated in mid-20th century tropical themed restaurants and tiki bars. The term "Tiki mugs" is a generic, blanket term for sculptural drink ware that depict imagery from Melanesia, Micronesia, or Polynesia, and more recently anything tropical or related to surfing. Often sold as souvenirs, tiki mugs are highly collectable. Modern manufacturers include Muntiki and Tiki Farm. Individual artists, such as Van Tiki, also produced limited one-of-a-kind hand sculpted mugs.[12]<br><br><u>Travel mugs</u><br><br>Travel mugs (introduced in the 1980s) generally employ thermal insulation properties for transporting hot or cold liquids. Similar to a vacuum flask, a travel mug is usually well-insulated and completely enclosed to prevent spillage[13] or leaking, but will generally have an opening in the cover through which the contents can be consumed during transportation without spillage. As the primary mechanism by which hot (not warm) beverages lose heat is evaporation, a lid serves a vital role in keeping the drink hot; even a thin plastic one which conducts heat quite quickly.<br><br>Mugs with inner and outer walls, but not vacuum treated, are generally called double wall mugs. Usually stainless steel will be used for the inner wall while outer wall can be stainless steel, plastic, or even embedded with other materials.<br><br>Mugs designed for use when driving are called auto mugs or commuter mugs. Travel mugs have a spill-proof lid with a sipping opening[14] and in many cases, a narrower base, so that they will fit into the cup-holders that are built into many vehicles.[citation needed] Additional criteria for evaluating auto mugs include: they must be easy to open single-handedly (to prevent distractions while driving), include a fill line (to prevent over-filling, which contributes to leaking), preferably have no handles (no-handled mugs are easier to grab while driving), should not obstruct a driver&#39;s view of the road when he or she is drinking, and - with regard to cup-holders be able to fit, stably, into a wide range of mug holders.[15][16][17]<br><br><br>',
          seo: {
            title: '',
            meta_description: '',
            url: ''
          },
          sections: [{
            i18nTitleTag: 'PRODUCTS.DESCRIPTION',
            content: '<u>Early mugs</u><br><br>Wooden mugs were produced probably from the earliest days of woodworking, but most of them have not survived intact.[3][4]<br>The first pottery was shaped by hand and was later facilitated by the invention of the potter&#39;s wheel (date unknown, between 6,500 and 3000 BCE). It was relatively easy to add a handle to a cup in the process thus producing a mug. For example, a rather advanced, decorated clay mug from 4000–5000 BCE was found in Greece.[5]<br>Ancestral Pueblo (Anasazi) mugs from SW Colorado, made between 1000 and 1280 CE. The meaning of the carving in the handles is as yet unknown, but it is probably not functional.<br>The biggest disadvantage of those clay mugs was thick walls unfit for the mouth. The walls were thinned with development of metalworking techniques. Metal mugs were produced from bronze,[6] silver, gold,[7] and even lead,[8] starting from roughly 2000 BCE, but were hard to use with hot drinks.<br>The invention of porcelain around 600 CE in China brought a new era of thin-walled mugs suitable both for cold and hot liquids, which are enjoyed today.<br><br><u>Shaving mug</u><br>A shaving scuttle and shaving mug were developed around the 19th century; the first patent for a shaving mug is dated 1867.[10] As hot water was not common in many households, one way to provide hot lather was to use a scuttle or mug. A traditional scuttle resembles a teapot with a wide spout where hot water is poured in; this is where it differs from a shaving mug, which has no spout. Both shaving scuttles and mugs usually have a handle, but some have none. Shaving mugs often look like a standard mug, however, some also have a built in brush rest, so the brush does not sit in lather. Modern versions of the scuttle are in limited production, usually by independent potters working in small volumes.[11]<br><br>At the top of the scuttle or mug is a soap holder. Traditionally, it was used with a hard block of shaving soap (rather than soft soap or cream) and therefore had drain holes at the bottom. Later scuttles and mugs do not include the holes, and thus can be used with creams and soft soaps. Some scuttles and mugs have concentric circles on the bottom, which retain some water thus helping to build lather.[11]<br><br>In use, the shaving brush is dunked into the wide spout, allowing it to soak into the water and heat up. The soap is placed in the soap holder. When needed, one can take the brush and brush it against the soap, bringing up a layer of lather; excess water is drained back. This allows conservation of water and soap, whilst retaining enough heat to ensure a long shave.<br><br><br><br><u>Tiki mugs</u><br>Tiki mugs, drinking vessels usually made of ceramics, originated in mid-20th century tropical themed restaurants and tiki bars. The term "Tiki mugs" is a generic, blanket term for sculptural drink ware that depict imagery from Melanesia, Micronesia, or Polynesia, and more recently anything tropical or related to surfing. Often sold as souvenirs, tiki mugs are highly collectable. Modern manufacturers include Muntiki and Tiki Farm. Individual artists, such as Van Tiki, also produced limited one-of-a-kind hand sculpted mugs.[12]<br><br><u>Travel mugs</u><br><br>Travel mugs (introduced in the 1980s) generally employ thermal insulation properties for transporting hot or cold liquids. Similar to a vacuum flask, a travel mug is usually well-insulated and completely enclosed to prevent spillage[13] or leaking, but will generally have an opening in the cover through which the contents can be consumed during transportation without spillage. As the primary mechanism by which hot (not warm) beverages lose heat is evaporation, a lid serves a vital role in keeping the drink hot; even a thin plastic one which conducts heat quite quickly.<br><br>Mugs with inner and outer walls, but not vacuum treated, are generally called double wall mugs. Usually stainless steel will be used for the inner wall while outer wall can be stainless steel, plastic, or even embedded with other materials.<br><br>Mugs designed for use when driving are called auto mugs or commuter mugs. Travel mugs have a spill-proof lid with a sipping opening[14] and in many cases, a narrower base, so that they will fit into the cup-holders that are built into many vehicles.[citation needed] Additional criteria for evaluating auto mugs include: they must be easy to open single-handedly (to prevent distractions while driving), include a fill line (to prevent over-filling, which contributes to leaking), preferably have no handles (no-handled mugs are easier to grab while driving), should not obstruct a driver&#39;s view of the road when he or she is drinking, and - with regard to cup-holders be able to fit, stably, into a wide range of mug holders.[15][16][17]<br><br><br>'
          }]
        }, {
          lang: 'ES',
          title: 'SP MUG01',
          short: '(SPANISH) A mug is a type of cup typically used for drinking hot drinks, such as coffee, hot chocolate, or tea. Mugs usually have handles and hold a larger amount of fluid than other types of cup. ... A travel mug is insulated and has a cover with a small sipping opening to prevent spills.',
          long: '(SPANISH LANGUAGE)<br><br>Early mugs<br><br>Wooden mugs were produced probably from the earliest days of woodworking, but most of them have not survived intact.[3][4]<br>The first pottery was shaped by hand and was later facilitated by the invention of the potter&#39;s wheel (date unknown, between 6,500 and 3000 BCE). It was relatively easy to add a handle to a cup in the process thus producing a mug. For example, a rather advanced, decorated clay mug from 4000–5000 BCE was found in Greece.[5]<br>Ancestral Pueblo (Anasazi) mugs from SW Colorado, made between 1000 and 1280 CE. The meaning of the carving in the handles is as yet unknown, but it is probably not functional.<br>The biggest disadvantage of those clay mugs was thick walls unfit for the mouth. The walls were thinned with development of metalworking techniques. Metal mugs were produced from bronze,[6] silver, gold,[7] and even lead,[8] starting from roughly 2000 BCE, but were hard to use with hot drinks.<br>The invention of porcelain around 600 CE in China brought a new era of thin-walled mugs suitable both for cold and hot liquids, which are enjoyed today.<br><br>Shaving mug<br>A shaving scuttle and shaving mug were developed around the 19th century; the first patent for a shaving mug is dated 1867.[10] As hot water was not common in many households, one way to provide hot lather was to use a scuttle or mug. A traditional scuttle resembles a teapot with a wide spout where hot water is poured in; this is where it differs from a shaving mug, which has no spout. Both shaving scuttles and mugs usually have a handle, but some have none. Shaving mugs often look like a standard mug, however, some also have a built in brush rest, so the brush does not sit in lather. Modern versions of the scuttle are in limited production, usually by independent potters working in small volumes.[11]<br><br>At the top of the scuttle or mug is a soap holder. Traditionally, it was used with a hard block of shaving soap (rather than soft soap or cream) and therefore had drain holes at the bottom. Later scuttles and mugs do not include the holes, and thus can be used with creams and soft soaps. Some scuttles and mugs have concentric circles on the bottom, which retain some water thus helping to build lather.[11]<br><br>In use, the shaving brush is dunked into the wide spout, allowing it to soak into the water and heat up. The soap is placed in the soap holder. When needed, one can take the brush and brush it against the soap, bringing up a layer of lather; excess water is drained back. This allows conservation of water and soap, whilst retaining enough heat to ensure a long shave.<br><br><br><br>Tiki mugs<br>Tiki mugs, drinking vessels usually made of ceramics, originated in mid-20th century tropical themed restaurants and tiki bars. The term "Tiki mugs" is a generic, blanket term for sculptural drink ware that depict imagery from Melanesia, Micronesia, or Polynesia, and more recently anything tropical or related to surfing. Often sold as souvenirs, tiki mugs are highly collectable. Modern manufacturers include Muntiki and Tiki Farm. Individual artists, such as Van Tiki, also produced limited one-of-a-kind hand sculpted mugs.[12]<br><br>Travel mugs<br><br>Travel mugs (introduced in the 1980s) generally employ thermal insulation properties for transporting hot or cold liquids. Similar to a vacuum flask, a travel mug is usually well-insulated and completely enclosed to prevent spillage[13] or leaking, but will generally have an opening in the cover through which the contents can be consumed during transportation without spillage. As the primary mechanism by which hot (not warm) beverages lose heat is evaporation, a lid serves a vital role in keeping the drink hot; even a thin plastic one which conducts heat quite quickly.<br><br>Mugs with inner and outer walls, but not vacuum treated, are generally called double wall mugs. Usually stainless steel will be used for the inner wall while outer wall can be stainless steel, plastic, or even embedded with other materials.<br><br>Mugs designed for use when driving are called auto mugs or commuter mugs. Travel mugs have a spill-proof lid with a sipping opening[14] and in many cases, a narrower base, so that they will fit into the cup-holders that are built into many vehicles.[citation needed] Additional criteria for evaluating auto mugs include: they must be easy to open single-handedly (to prevent distractions while driving), include a fill line (to prevent over-filling, which contributes to leaking), preferably have no handles (no-handled mugs are easier to grab while driving), should not obstruct a driver&#39;s view of the road when he or she is drinking, and - with regard to cup-holders be able to fit, stably, into a wide range of mug holders.[15][16][17]',
          seo: {
            title: '',
            meta_description: '',
            url: ''
          },
          sections: [{
            i18nTitleTag: 'PRODUCTS.DESCRIPTION',
            content: '(SPANISH LANGUAGE)<br><br>Early mugs<br><br>Wooden mugs were produced probably from the earliest days of woodworking, but most of them have not survived intact.[3][4]<br>The first pottery was shaped by hand and was later facilitated by the invention of the potter&#39;s wheel (date unknown, between 6,500 and 3000 BCE). It was relatively easy to add a handle to a cup in the process thus producing a mug. For example, a rather advanced, decorated clay mug from 4000–5000 BCE was found in Greece.[5]<br>Ancestral Pueblo (Anasazi) mugs from SW Colorado, made between 1000 and 1280 CE. The meaning of the carving in the handles is as yet unknown, but it is probably not functional.<br>The biggest disadvantage of those clay mugs was thick walls unfit for the mouth. The walls were thinned with development of metalworking techniques. Metal mugs were produced from bronze,[6] silver, gold,[7] and even lead,[8] starting from roughly 2000 BCE, but were hard to use with hot drinks.<br>The invention of porcelain around 600 CE in China brought a new era of thin-walled mugs suitable both for cold and hot liquids, which are enjoyed today.<br><br>Shaving mug<br>A shaving scuttle and shaving mug were developed around the 19th century; the first patent for a shaving mug is dated 1867.[10] As hot water was not common in many households, one way to provide hot lather was to use a scuttle or mug. A traditional scuttle resembles a teapot with a wide spout where hot water is poured in; this is where it differs from a shaving mug, which has no spout. Both shaving scuttles and mugs usually have a handle, but some have none. Shaving mugs often look like a standard mug, however, some also have a built in brush rest, so the brush does not sit in lather. Modern versions of the scuttle are in limited production, usually by independent potters working in small volumes.[11]<br><br>At the top of the scuttle or mug is a soap holder. Traditionally, it was used with a hard block of shaving soap (rather than soft soap or cream) and therefore had drain holes at the bottom. Later scuttles and mugs do not include the holes, and thus can be used with creams and soft soaps. Some scuttles and mugs have concentric circles on the bottom, which retain some water thus helping to build lather.[11]<br><br>In use, the shaving brush is dunked into the wide spout, allowing it to soak into the water and heat up. The soap is placed in the soap holder. When needed, one can take the brush and brush it against the soap, bringing up a layer of lather; excess water is drained back. This allows conservation of water and soap, whilst retaining enough heat to ensure a long shave.<br><br><br><br>Tiki mugs<br>Tiki mugs, drinking vessels usually made of ceramics, originated in mid-20th century tropical themed restaurants and tiki bars. The term "Tiki mugs" is a generic, blanket term for sculptural drink ware that depict imagery from Melanesia, Micronesia, or Polynesia, and more recently anything tropical or related to surfing. Often sold as souvenirs, tiki mugs are highly collectable. Modern manufacturers include Muntiki and Tiki Farm. Individual artists, such as Van Tiki, also produced limited one-of-a-kind hand sculpted mugs.[12]<br><br>Travel mugs<br><br>Travel mugs (introduced in the 1980s) generally employ thermal insulation properties for transporting hot or cold liquids. Similar to a vacuum flask, a travel mug is usually well-insulated and completely enclosed to prevent spillage[13] or leaking, but will generally have an opening in the cover through which the contents can be consumed during transportation without spillage. As the primary mechanism by which hot (not warm) beverages lose heat is evaporation, a lid serves a vital role in keeping the drink hot; even a thin plastic one which conducts heat quite quickly.<br><br>Mugs with inner and outer walls, but not vacuum treated, are generally called double wall mugs. Usually stainless steel will be used for the inner wall while outer wall can be stainless steel, plastic, or even embedded with other materials.<br><br>Mugs designed for use when driving are called auto mugs or commuter mugs. Travel mugs have a spill-proof lid with a sipping opening[14] and in many cases, a narrower base, so that they will fit into the cup-holders that are built into many vehicles.[citation needed] Additional criteria for evaluating auto mugs include: they must be easy to open single-handedly (to prevent distractions while driving), include a fill line (to prevent over-filling, which contributes to leaking), preferably have no handles (no-handled mugs are easier to grab while driving), should not obstruct a driver&#39;s view of the road when he or she is drinking, and - with regard to cup-holders be able to fit, stably, into a wide range of mug holders.[15][16][17]'
          }]
        }, {
          lang: 'DE',
          title: 'GR MUG01',
          short: '(GERMAN) A mug is a type of cup typically used for drinking hot drinks, such as coffee, hot chocolate, or tea. Mugs usually have handles and hold a larger amount of fluid than other types of cup. ... A travel mug is insulated and has a cover with a small sipping opening to prevent spills.',
          long: '(GERMAN LANGUAGE)<br><br><br>Early mugs<br><br>Wooden mugs were produced probably from the earliest days of woodworking, but most of them have not survived intact.[3][4]<br>The first pottery was shaped by hand and was later facilitated by the invention of the potter&#39;s wheel (date unknown, between 6,500 and 3000 BCE). It was relatively easy to add a handle to a cup in the process thus producing a mug. For example, a rather advanced, decorated clay mug from 4000–5000 BCE was found in Greece.[5]<br>Ancestral Pueblo (Anasazi) mugs from SW Colorado, made between 1000 and 1280 CE. The meaning of the carving in the handles is as yet unknown, but it is probably not functional.<br>The biggest disadvantage of those clay mugs was thick walls unfit for the mouth. The walls were thinned with development of metalworking techniques. Metal mugs were produced from bronze,[6] silver, gold,[7] and even lead,[8] starting from roughly 2000 BCE, but were hard to use with hot drinks.<br>The invention of porcelain around 600 CE in China brought a new era of thin-walled mugs suitable both for cold and hot liquids, which are enjoyed today.<br><br>Shaving mug<br>A shaving scuttle and shaving mug were developed around the 19th century; the first patent for a shaving mug is dated 1867.[10] As hot water was not common in many households, one way to provide hot lather was to use a scuttle or mug. A traditional scuttle resembles a teapot with a wide spout where hot water is poured in; this is where it differs from a shaving mug, which has no spout. Both shaving scuttles and mugs usually have a handle, but some have none. Shaving mugs often look like a standard mug, however, some also have a built in brush rest, so the brush does not sit in lather. Modern versions of the scuttle are in limited production, usually by independent potters working in small volumes.[11]<br><br>At the top of the scuttle or mug is a soap holder. Traditionally, it was used with a hard block of shaving soap (rather than soft soap or cream) and therefore had drain holes at the bottom. Later scuttles and mugs do not include the holes, and thus can be used with creams and soft soaps. Some scuttles and mugs have concentric circles on the bottom, which retain some water thus helping to build lather.[11]<br><br>In use, the shaving brush is dunked into the wide spout, allowing it to soak into the water and heat up. The soap is placed in the soap holder. When needed, one can take the brush and brush it against the soap, bringing up a layer of lather; excess water is drained back. This allows conservation of water and soap, whilst retaining enough heat to ensure a long shave.<br><br><br><br>Tiki mugs<br>Tiki mugs, drinking vessels usually made of ceramics, originated in mid-20th century tropical themed restaurants and tiki bars. The term "Tiki mugs" is a generic, blanket term for sculptural drink ware that depict imagery from Melanesia, Micronesia, or Polynesia, and more recently anything tropical or related to surfing. Often sold as souvenirs, tiki mugs are highly collectable. Modern manufacturers include Muntiki and Tiki Farm. Individual artists, such as Van Tiki, also produced limited one-of-a-kind hand sculpted mugs.[12]<br><br>Travel mugs<br><br>Travel mugs (introduced in the 1980s) generally employ thermal insulation properties for transporting hot or cold liquids. Similar to a vacuum flask, a travel mug is usually well-insulated and completely enclosed to prevent spillage[13] or leaking, but will generally have an opening in the cover through which the contents can be consumed during transportation without spillage. As the primary mechanism by which hot (not warm) beverages lose heat is evaporation, a lid serves a vital role in keeping the drink hot; even a thin plastic one which conducts heat quite quickly.<br><br>Mugs with inner and outer walls, but not vacuum treated, are generally called double wall mugs. Usually stainless steel will be used for the inner wall while outer wall can be stainless steel, plastic, or even embedded with other materials.<br><br>Mugs designed for use when driving are called auto mugs or commuter mugs. Travel mugs have a spill-proof lid with a sipping opening[14] and in many cases, a narrower base, so that they will fit into the cup-holders that are built into many vehicles.[citation needed] Additional criteria for evaluating auto mugs include: they must be easy to open single-handedly (to prevent distractions while driving), include a fill line (to prevent over-filling, which contributes to leaking), preferably have no handles (no-handled mugs are easier to grab while driving), should not obstruct a driver&#39;s view of the road when he or she is drinking, and - with regard to cup-holders be able to fit, stably, into a wide range of mug holders.[15][16][17]',
          seo: {
            title: '',
            meta_description: '',
            url: ''
          },
          sections: [{
            i18nTitleTag: 'PRODUCTS.DESCRIPTION',
            content: '(GERMAN LANGUAGE)<br><br><br>Early mugs<br><br>Wooden mugs were produced probably from the earliest days of woodworking, but most of them have not survived intact.[3][4]<br>The first pottery was shaped by hand and was later facilitated by the invention of the potter&#39;s wheel (date unknown, between 6,500 and 3000 BCE). It was relatively easy to add a handle to a cup in the process thus producing a mug. For example, a rather advanced, decorated clay mug from 4000–5000 BCE was found in Greece.[5]<br>Ancestral Pueblo (Anasazi) mugs from SW Colorado, made between 1000 and 1280 CE. The meaning of the carving in the handles is as yet unknown, but it is probably not functional.<br>The biggest disadvantage of those clay mugs was thick walls unfit for the mouth. The walls were thinned with development of metalworking techniques. Metal mugs were produced from bronze,[6] silver, gold,[7] and even lead,[8] starting from roughly 2000 BCE, but were hard to use with hot drinks.<br>The invention of porcelain around 600 CE in China brought a new era of thin-walled mugs suitable both for cold and hot liquids, which are enjoyed today.<br><br>Shaving mug<br>A shaving scuttle and shaving mug were developed around the 19th century; the first patent for a shaving mug is dated 1867.[10] As hot water was not common in many households, one way to provide hot lather was to use a scuttle or mug. A traditional scuttle resembles a teapot with a wide spout where hot water is poured in; this is where it differs from a shaving mug, which has no spout. Both shaving scuttles and mugs usually have a handle, but some have none. Shaving mugs often look like a standard mug, however, some also have a built in brush rest, so the brush does not sit in lather. Modern versions of the scuttle are in limited production, usually by independent potters working in small volumes.[11]<br><br>At the top of the scuttle or mug is a soap holder. Traditionally, it was used with a hard block of shaving soap (rather than soft soap or cream) and therefore had drain holes at the bottom. Later scuttles and mugs do not include the holes, and thus can be used with creams and soft soaps. Some scuttles and mugs have concentric circles on the bottom, which retain some water thus helping to build lather.[11]<br><br>In use, the shaving brush is dunked into the wide spout, allowing it to soak into the water and heat up. The soap is placed in the soap holder. When needed, one can take the brush and brush it against the soap, bringing up a layer of lather; excess water is drained back. This allows conservation of water and soap, whilst retaining enough heat to ensure a long shave.<br><br><br><br>Tiki mugs<br>Tiki mugs, drinking vessels usually made of ceramics, originated in mid-20th century tropical themed restaurants and tiki bars. The term "Tiki mugs" is a generic, blanket term for sculptural drink ware that depict imagery from Melanesia, Micronesia, or Polynesia, and more recently anything tropical or related to surfing. Often sold as souvenirs, tiki mugs are highly collectable. Modern manufacturers include Muntiki and Tiki Farm. Individual artists, such as Van Tiki, also produced limited one-of-a-kind hand sculpted mugs.[12]<br><br>Travel mugs<br><br>Travel mugs (introduced in the 1980s) generally employ thermal insulation properties for transporting hot or cold liquids. Similar to a vacuum flask, a travel mug is usually well-insulated and completely enclosed to prevent spillage[13] or leaking, but will generally have an opening in the cover through which the contents can be consumed during transportation without spillage. As the primary mechanism by which hot (not warm) beverages lose heat is evaporation, a lid serves a vital role in keeping the drink hot; even a thin plastic one which conducts heat quite quickly.<br><br>Mugs with inner and outer walls, but not vacuum treated, are generally called double wall mugs. Usually stainless steel will be used for the inner wall while outer wall can be stainless steel, plastic, or even embedded with other materials.<br><br>Mugs designed for use when driving are called auto mugs or commuter mugs. Travel mugs have a spill-proof lid with a sipping opening[14] and in many cases, a narrower base, so that they will fit into the cup-holders that are built into many vehicles.[citation needed] Additional criteria for evaluating auto mugs include: they must be easy to open single-handedly (to prevent distractions while driving), include a fill line (to prevent over-filling, which contributes to leaking), preferably have no handles (no-handled mugs are easier to grab while driving), should not obstruct a driver&#39;s view of the road when he or she is drinking, and - with regard to cup-holders be able to fit, stably, into a wide range of mug holders.[15][16][17]'
          }]
        }, {
          lang: 'IT',
          title: 'IT MUG01',
          short: '(ITALIAN) A mug is a type of cup typically used for drinking hot drinks, such as coffee, hot chocolate, or tea. Mugs usually have handles and hold a larger amount of fluid than other types of cup. ... A travel mug is insulated and has a cover with a small sipping opening to prevent spills.',
          long: '(ITALIAN LANGUAGE)<br><br>Early mugs<br><br>Wooden mugs were produced probably from the earliest days of woodworking, but most of them have not survived intact.[3][4]<br>The first pottery was shaped by hand and was later facilitated by the invention of the potter&#39;s wheel (date unknown, between 6,500 and 3000 BCE). It was relatively easy to add a handle to a cup in the process thus producing a mug. For example, a rather advanced, decorated clay mug from 4000–5000 BCE was found in Greece.[5]<br>Ancestral Pueblo (Anasazi) mugs from SW Colorado, made between 1000 and 1280 CE. The meaning of the carving in the handles is as yet unknown, but it is probably not functional.<br>The biggest disadvantage of those clay mugs was thick walls unfit for the mouth. The walls were thinned with development of metalworking techniques. Metal mugs were produced from bronze,[6] silver, gold,[7] and even lead,[8] starting from roughly 2000 BCE, but were hard to use with hot drinks.<br>The invention of porcelain around 600 CE in China brought a new era of thin-walled mugs suitable both for cold and hot liquids, which are enjoyed today.<br><br>Shaving mug<br>A shaving scuttle and shaving mug were developed around the 19th century; the first patent for a shaving mug is dated 1867.[10] As hot water was not common in many households, one way to provide hot lather was to use a scuttle or mug. A traditional scuttle resembles a teapot with a wide spout where hot water is poured in; this is where it differs from a shaving mug, which has no spout. Both shaving scuttles and mugs usually have a handle, but some have none. Shaving mugs often look like a standard mug, however, some also have a built in brush rest, so the brush does not sit in lather. Modern versions of the scuttle are in limited production, usually by independent potters working in small volumes.[11]<br><br>At the top of the scuttle or mug is a soap holder. Traditionally, it was used with a hard block of shaving soap (rather than soft soap or cream) and therefore had drain holes at the bottom. Later scuttles and mugs do not include the holes, and thus can be used with creams and soft soaps. Some scuttles and mugs have concentric circles on the bottom, which retain some water thus helping to build lather.[11]<br><br>In use, the shaving brush is dunked into the wide spout, allowing it to soak into the water and heat up. The soap is placed in the soap holder. When needed, one can take the brush and brush it against the soap, bringing up a layer of lather; excess water is drained back. This allows conservation of water and soap, whilst retaining enough heat to ensure a long shave.<br><br><br><br>Tiki mugs<br>Tiki mugs, drinking vessels usually made of ceramics, originated in mid-20th century tropical themed restaurants and tiki bars. The term "Tiki mugs" is a generic, blanket term for sculptural drink ware that depict imagery from Melanesia, Micronesia, or Polynesia, and more recently anything tropical or related to surfing. Often sold as souvenirs, tiki mugs are highly collectable. Modern manufacturers include Muntiki and Tiki Farm. Individual artists, such as Van Tiki, also produced limited one-of-a-kind hand sculpted mugs.[12]<br><br>Travel mugs<br><br>Travel mugs (introduced in the 1980s) generally employ thermal insulation properties for transporting hot or cold liquids. Similar to a vacuum flask, a travel mug is usually well-insulated and completely enclosed to prevent spillage[13] or leaking, but will generally have an opening in the cover through which the contents can be consumed during transportation without spillage. As the primary mechanism by which hot (not warm) beverages lose heat is evaporation, a lid serves a vital role in keeping the drink hot; even a thin plastic one which conducts heat quite quickly.<br><br>Mugs with inner and outer walls, but not vacuum treated, are generally called double wall mugs. Usually stainless steel will be used for the inner wall while outer wall can be stainless steel, plastic, or even embedded with other materials.<br><br>Mugs designed for use when driving are called auto mugs or commuter mugs. Travel mugs have a spill-proof lid with a sipping opening[14] and in many cases, a narrower base, so that they will fit into the cup-holders that are built into many vehicles.[citation needed] Additional criteria for evaluating auto mugs include: they must be easy to open single-handedly (to prevent distractions while driving), include a fill line (to prevent over-filling, which contributes to leaking), preferably have no handles (no-handled mugs are easier to grab while driving), should not obstruct a driver&#39;s view of the road when he or she is drinking, and - with regard to cup-holders be able to fit, stably, into a wide range of mug holders.[15][16][17]',
          seo: {
            title: '',
            meta_description: '',
            url: ''
          },
          sections: [{
            i18nTitleTag: 'PRODUCTS.DESCRIPTION',
            content: '(ITALIAN LANGUAGE)<br><br>Early mugs<br><br>Wooden mugs were produced probably from the earliest days of woodworking, but most of them have not survived intact.[3][4]<br>The first pottery was shaped by hand and was later facilitated by the invention of the potter&#39;s wheel (date unknown, between 6,500 and 3000 BCE). It was relatively easy to add a handle to a cup in the process thus producing a mug. For example, a rather advanced, decorated clay mug from 4000–5000 BCE was found in Greece.[5]<br>Ancestral Pueblo (Anasazi) mugs from SW Colorado, made between 1000 and 1280 CE. The meaning of the carving in the handles is as yet unknown, but it is probably not functional.<br>The biggest disadvantage of those clay mugs was thick walls unfit for the mouth. The walls were thinned with development of metalworking techniques. Metal mugs were produced from bronze,[6] silver, gold,[7] and even lead,[8] starting from roughly 2000 BCE, but were hard to use with hot drinks.<br>The invention of porcelain around 600 CE in China brought a new era of thin-walled mugs suitable both for cold and hot liquids, which are enjoyed today.<br><br>Shaving mug<br>A shaving scuttle and shaving mug were developed around the 19th century; the first patent for a shaving mug is dated 1867.[10] As hot water was not common in many households, one way to provide hot lather was to use a scuttle or mug. A traditional scuttle resembles a teapot with a wide spout where hot water is poured in; this is where it differs from a shaving mug, which has no spout. Both shaving scuttles and mugs usually have a handle, but some have none. Shaving mugs often look like a standard mug, however, some also have a built in brush rest, so the brush does not sit in lather. Modern versions of the scuttle are in limited production, usually by independent potters working in small volumes.[11]<br><br>At the top of the scuttle or mug is a soap holder. Traditionally, it was used with a hard block of shaving soap (rather than soft soap or cream) and therefore had drain holes at the bottom. Later scuttles and mugs do not include the holes, and thus can be used with creams and soft soaps. Some scuttles and mugs have concentric circles on the bottom, which retain some water thus helping to build lather.[11]<br><br>In use, the shaving brush is dunked into the wide spout, allowing it to soak into the water and heat up. The soap is placed in the soap holder. When needed, one can take the brush and brush it against the soap, bringing up a layer of lather; excess water is drained back. This allows conservation of water and soap, whilst retaining enough heat to ensure a long shave.<br><br><br><br>Tiki mugs<br>Tiki mugs, drinking vessels usually made of ceramics, originated in mid-20th century tropical themed restaurants and tiki bars. The term "Tiki mugs" is a generic, blanket term for sculptural drink ware that depict imagery from Melanesia, Micronesia, or Polynesia, and more recently anything tropical or related to surfing. Often sold as souvenirs, tiki mugs are highly collectable. Modern manufacturers include Muntiki and Tiki Farm. Individual artists, such as Van Tiki, also produced limited one-of-a-kind hand sculpted mugs.[12]<br><br>Travel mugs<br><br>Travel mugs (introduced in the 1980s) generally employ thermal insulation properties for transporting hot or cold liquids. Similar to a vacuum flask, a travel mug is usually well-insulated and completely enclosed to prevent spillage[13] or leaking, but will generally have an opening in the cover through which the contents can be consumed during transportation without spillage. As the primary mechanism by which hot (not warm) beverages lose heat is evaporation, a lid serves a vital role in keeping the drink hot; even a thin plastic one which conducts heat quite quickly.<br><br>Mugs with inner and outer walls, but not vacuum treated, are generally called double wall mugs. Usually stainless steel will be used for the inner wall while outer wall can be stainless steel, plastic, or even embedded with other materials.<br><br>Mugs designed for use when driving are called auto mugs or commuter mugs. Travel mugs have a spill-proof lid with a sipping opening[14] and in many cases, a narrower base, so that they will fit into the cup-holders that are built into many vehicles.[citation needed] Additional criteria for evaluating auto mugs include: they must be easy to open single-handedly (to prevent distractions while driving), include a fill line (to prevent over-filling, which contributes to leaking), preferably have no handles (no-handled mugs are easier to grab while driving), should not obstruct a driver&#39;s view of the road when he or she is drinking, and - with regard to cup-holders be able to fit, stably, into a wide range of mug holders.[15][16][17]'
          }]
        }],
        hiddenPrice: false,
        id: '676685',
        imageUrl: 'https://d2f3d56fenwb91.cloudfront.net/public/assets/user/15105302/image/7a942d3cb87df23bd3e009e7cce4038a.png',
        price: {
          country: 'REU',
          currency: 'EUR',
          currencies: [{
            currency: 'EUR',
            prices: [{
              recommendedPrice: '0',
              unitCost: '0',
              licence: 'NL',
              price: 10,
              oldPrice: 20
            }]
          }],
          includesTaxes: true,
          prices: [{
            licence: 'NL',
            price: 10,
            crossed_price: 20
          }],
          taxType: 'default'
        },
        gallery: [{
          name: '',
          nameAlt: '',
          type: 'file',
          url: 'https://d2f3d56fenwb91.cloudfront.net/public/assets/user/15105302/image/7a942d3cb87df23bd3e009e7cce4038a.png',
          visible: true
        }, {
          name: '',
          nameAlt: '',
          type: 'file',
          url: 'https://d2f3d56fenwb91.cloudfront.net/public/assets/user/15105302/sample/93a721e2aa25579915e5671893b450c8.jpg',
          visible: true
        }],
        state: 'VALIDATED',
        stock: {
          lowStockAlert: 10,
          policy: 'SUSPEND',
          stock: 100
        },
        type: 'PHYSICAL'
      }]
    };
  }
  get() {
    return this.config;
  }
}
class LocalizationConfigServiceMock {
  constructor() {
    this.config = {
      v4Locale: '',
      currency: 'USD',
      territories: ['REU'],
      currentLang: 'US'
    };
  }
  get() {
    return this.config;
  }
}
class PlatformServiceMock {
  constructor() {
    this.isBrowser = () => true;
    this.isServer = () => false; // return false always, unless you use sam from node, in that case it should return true
    this.isServerSideRendering = () => false;
  }
}
class CookieServiceMock {
  constructor() {
    this.memory = {};
  }
  check(key) {
    return !!this.memory[key];
  }
  set(key, value) {
    this.memory[key] = value;
  }
  delete(key) {
    delete this.memory[key];
  }
  get(key) {
    return this.memory[key];
  }
}
class httpClient {
  constructor() {
    this.get = () => {
      return of();
    };
    this.put = () => {
      return of('');
    };
    this.post = () => {
      return of('');
    };
    this.delete = () => {
      return of('');
    };
  }
}
class SamAngularServiceMock {
  constructor(jest = null) {
    this.httpClient = new HttpClient(new HttpXhrBackend({
      build: () => new XMLHttpRequest()
    }));
    this.config = {
      apiContext: {
        v4: {
          clientId: 'A233F84AB42713CF4C54AE372FA5E3AE',
          assetsUrl: 'https://d2f3d56fenwb91.cloudfront.net/public/assets',
          projectId: '1246',
          territoryId: 'IT',
          apiKey: '61DDBC7A51AE2803A47A3AF4EBF239AA',
          store_id: 2,
          zendeskGetFormBaseEndpoint: '/zendesk/ticket_form',
          zendeskSendFormBaseEndpoint: '/zendesk/ticket',
          zendeskFormID: '5789758078994',
          storeSearchBaseUrl: 'https://2s8gancjl1.execute-api.eu-central-1.amazonaws.com/default/StoreSearch',
          pepStorageKey: '',
          baseUrl: 'https://frontv5testing-api.scalefast.ninja'
        },
        v5: {
          clientSecret: 'ec3c63ec407ac738af5df0a67f12ef894179464d89767c7bdf124818980e411e',
          projectId: '1246',
          territoryId: 'IT',
          apiKey: '5457D4CDE0F28FC5EA15A07F65EF4DAF',
          store_id: 2,
          grantTypeM2M: 'password',
          grantTypeStore: 'client_credentials',
          clientIdM2M: 'c068011f63cf932a47096317a86dea7b',
          clientIdStore: '8d8e0cdb52b3d0f1d7553312e1e82385',
          baseUrl: 'https://frontv5testing-api.scalefast.ninja',
          paymentBaseUrl: 'https://pay-v5.scalefast.ninja',
          komojuBaseUrl: 'https://komoju.com'
        },
        v4StoreSearch: {
          userID: '123622',
          env: ''
        }
      },
      drivers: {
        httpClient: this.httpClient,
        cookieService: new CookieServiceMock(),
        platformService: new PlatformServiceMock()
      },
      credentialsConfig: {
        accessToken: {
          implementation: 'local-storage',
          expiration: 100000
        }
      },
      cartStorageConfig: {
        implementation: 'x-api'
      }
    };
    this.getContext = () => {
      return this.config;
    };
    if (jest) {
      this.httpClient = new httpClient();
      this.getSpy = jest.spyOn(this.httpClient, 'get');
      this.getSpy.mockReturnValue(of(''));
      this.postSpy = jest.spyOn(this.httpClient, 'post');
      this.postSpy.mockReturnValue(of(''));
    }
  }
}
let WishlistServiceMock = /*#__PURE__*/(() => {
  class WishlistServiceMock {
    constructor() {
      this.wishlist = WishlistServiceMock.Response.getWishList();
    }
    getWishlist$() {
      return of(this.wishlist);
    }
    addIntoWishlist$(wishlist, product) {
      return of({
        result: {
          status: '200',
          errcode: '0',
          msg: 'This is a mock'
        },
        response_data: true
      });
    }
    removeFromWishlist$(wishlist, product) {
      wishlist.products.filter(wishlistProduct => {
        return wishlistProduct.productID !== product.id;
      });
      return of({
        result: {
          status: '200',
          errcode: '0',
          msg: 'This is a mock'
        },
        response_data: true
      });
    }
    checkLoggedUser() {
      return;
    }
    static {
      this.Response = class {
        static getWishList() {
          return {
            id: '9',
            userID: '1212',
            quantity: '1',
            notificationType: 'none',
            type: 'cart',
            currency: 'USD',
            price: '20',
            state: '1',
            date: '',
            products: []
          };
        }
      };
    }
  }
  return WishlistServiceMock;
})();
class StoreSpecificConfigServiceMock {
  constructor() {
    this.config = {
      buildId: '1706619235',
      integrations: {
        bazaarvoice: {}
      }
    };
  }
  get() {
    return this.config;
  }
}
class FeatureFlagsConfigServiceMock {
  constructor() {
    this.config = {
      filterPOHProductsInSearch: false
    };
  }
  get() {
    return this.config;
  }
}

/** @TODO Create a barrel export for all of these */

/**
 * Generated bundle index. Do not edit.
 */

export { ActivatedRouterWithFiltersMock, BaseConfigService, BffConfigService, BuilderConfigService, CONFIG_LOADER, CheckoutConfigService, CheckoutConfigServiceMock, ConfigModule, ContentfulConfigService, ContentfulConfigServiceMock, ContentfulResolverMock, CustomerServiceMock, FeatureFlagsConfigService, FeatureFlagsConfigServiceMock, FilteredProductsMock, FiltersConfigService, FiltersConfigServiceMock, FiltersCriteriaMock, LegacyConfigService, LegacyConfigServiceMock, LocalizationConfigService, LocalizationConfigServiceMock, MaintenanceConfigService, SamAngularServiceMock, SamConfigSchema, SamConfigService, SamConfigServiceMock, StoreConfigService, StoreConfigServiceMock, StoreSearchConfigService, StoreSearchConfigServiceMock, StoreSearchParamsMock, StoreSpecificConfigService, StoreSpecificConfigServiceMock, WishlistServiceMock };
